import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Input, Row, Select, Space, Spin, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import EditableField from 'src/components/fields/EditableField';
import { DeleteOutlined, ShopTwoTone, UserOutlined } from '@ant-design/icons';
import { useAccountById } from 'src/features/companyAccounts/companyAccounts.hooks';
import { deleteAccount, fetchAccount, updateAccount } from 'src/features/companyAccounts/companyAccounts.state';
import { CompanyAccount } from 'src/features/companyAccounts/companyAccounts.types';
import { AppDispatch } from 'src/index';
import tileStyles from 'src/styles/tile.module.css';

const { Option } = Select;

const CompanyAccountPage: React.FC = () => {
  const { accountId } = useParams() as { accountId: string };
  const dispatch: AppDispatch = useDispatch();
  const account = useAccountById(accountId) as CompanyAccount;

  const [draftAccount, setDraftAccount] = useState<Partial<CompanyAccount>>({});

  const handleDeleteButtonClick = useCallback(() => {
    dispatch(deleteAccount({ accountId }));
  }, [dispatch, accountId]);

  const handleCompanyAccountUpdate = useCallback(
    async (data: typeof draftAccount) => {
      const response = await dispatch(updateAccount({ accountId, data }));

      if (updateAccount.rejected.match(response)) {
        setDraftAccount({});
      }
    },
    [dispatch, accountId]
  );

  useEffect(() => {
    setDraftAccount({});
  }, [account]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/accounts'>
                <Space>
                  <UserOutlined />
                  Аккаунты компаний
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/commercial/accounts/${accountId}`}>{account.fullname}</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {account.fullname}
          </Typography.Title>
          <Space size='small' />
          <div>
            {account.companies?.map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/commercial/companies/${c.id}`} className={tileStyles.tileName}>
                  <Space>
                    <ShopTwoTone />
                    {c.name}
                  </Space>
                </Link>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button
              type='primary'
              onClick={() => {
                handleCompanyAccountUpdate({ disabled: !account.disabled });
              }}
              danger={!account.disabled}
            >
              {account.disabled ? 'Разблокировать' : 'Заблокировать'}
            </Button>
          </Space>
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>Имя</Typography.Text>
            <EditableField value={account.fullname}>
              <Input
                name='fullname'
                value={draftAccount.fullname}
                placeholder='Введите имя'
                onChange={ev => setDraftAccount({ fullname: ev.target.value })}
                onBlur={() => handleCompanyAccountUpdate(draftAccount)}
              />
            </EditableField>
            <Typography.Text className={styles.title}>Почта</Typography.Text>
            <EditableField value={account.email}>
              <Input
                type='email'
                name='email'
                value={draftAccount.email}
                placeholder='Введите e-mail'
                onChange={ev => setDraftAccount({ email: ev.target.value })}
                onBlur={() => handleCompanyAccountUpdate(draftAccount)}
              />
            </EditableField>
            <Typography.Text className={styles.title}>Пароль</Typography.Text>
            <EditableField value='Сменить пароль'>
              <Input
                type='password'
                name='password'
                placeholder='Введите пароль'
                onChange={ev => setDraftAccount({ password: ev.target.value })}
                onBlur={() => handleCompanyAccountUpdate(draftAccount)}
              />
            </EditableField>
            <Typography.Text className={styles.title}>Роль</Typography.Text>
            <EditableField value={account.role}>
              <Select
                value={account.role}
                onSelect={e => handleCompanyAccountUpdate({ role: e })}
                style={{ width: '100%' }}
                defaultOpen
              >
                {[
                  { label: 'Администратор', value: 'admin' },
                  { label: 'Бухгалтер', value: 'accountant' },
                  { label: 'Инженер', value: 'engineer' },
                  { label: 'Наблюдатель', value: 'observer' },
                  { label: 'Ресепшен', value: 'reception' },
                  { label: 'Airpano', value: 'airpano' },
                ].map(l => (
                  <Option key={l.label} value={l.value}>
                    {l.label}
                  </Option>
                ))}
              </Select>
            </EditableField>
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            {/*<Typography.Text className={styles.footerDateStamp}>Создано: {parseDate(account.createdAt)}</Typography.Text>*/}
            {/*<Typography.Text className={styles.footerDateStamp}>Обновлено: {parseDate(account.updatedAt)}</Typography.Text>*/}
            <Button type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
              <DeleteOutlined /> Удалить аккаунт
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CompanyAccountPageBlock: React.FC = () => {
  const { accountId } = useParams() as { accountId: string };
  const dispatch = useDispatch();
  const account = useAccountById(accountId);
  useEffect(() => {
    dispatch(fetchAccount({ accountId }));
  }, [dispatch, accountId]);

  if (!account) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <CompanyAccountPage />;
};

export default CompanyAccountPageBlock;
